// src/App.js
import { Component } from 'react';
import { HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './hooks/useAuth';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(err, info) { console.error('App crash:', err, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Noto Sans, sans-serif', padding: 24 }}>
          <div style={{ textAlign: 'center', maxWidth: 360 }}>
            <div style={{ width: 52, height: 52, borderRadius: '50%', background: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            </div>
            <h1 style={{ fontSize: '1.3rem', marginBottom: 8, fontFamily: 'Noto Sans, sans-serif' }}>Something went wrong</h1>
            <p style={{ color: '#64748B', fontSize: '0.88rem', marginBottom: 20 }}>Please refresh the page to try again.</p>
            <button onClick={() => window.location.reload()} style={{
              background: '#0F172A', color: 'white', border: 'none', borderRadius: 10,
              padding: '12px 28px', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer',
              fontFamily: 'Noto Sans, sans-serif',
            }}>Refresh</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import PricingPage from './pages/PricingPage';
import CreateAgreementPage from './pages/CreateAgreementPage';
import PendingConfirmationPage from './pages/PendingConfirmationPage';
import ParticipantConfirmationPage from './pages/ParticipantConfirmationPage';
import AgreementConfirmedPage from './pages/AgreementConfirmedPage';
import AgreementDeclinedPage from './pages/AgreementDeclinedPage';
import DashboardPage from './pages/DashboardPage';
import PremiumPage from './pages/PremiumPage';
import NotFoundPage from './pages/NotFoundPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import AccountPage from './pages/AccountPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SetPasswordPage from './pages/SetPasswordPage';
import SupportPage from './pages/SupportPage';

import Footer from './components/Footer';
import SmartAppBanner from './components/SmartAppBanner';
import { FULL_ACCESS_MODE } from './config/flags';
import './styles/global.css';

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  // Block unverified email users (Google users are always verified)
  if (!user.emailVerified) return <Navigate to="/verify-email" replace />;
  return children;
};

const AppRoutes = () => (
  <Routes>
    {/* Public */}
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/signup" element={<SignupPage />} />
    <Route path="/pricing" element={FULL_ACCESS_MODE ? <Navigate to="/" replace /> : <PricingPage />} />
    <Route path="/premium" element={FULL_ACCESS_MODE ? <Navigate to="/" replace /> : <PremiumPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/terms" element={<TermsPage />} />
    <Route path="/privacy" element={<PrivacyPage />} />
    <Route path="/set-password" element={<SetPasswordPage />} />
    <Route path="/support" element={<SupportPage />} />

    {/* Partner flow — NO auth required */}
    <Route path="/confirm/:agreementID" element={<ParticipantConfirmationPage />} />
    <Route path="/agreement-confirmed/:agreementID" element={<AgreementConfirmedPage />} />
    <Route path="/agreement-declined/:agreementID" element={<AgreementDeclinedPage />} />

    {/* Protected */}
    <Route path="/create" element={<ProtectedRoute><CreateAgreementPage /></ProtectedRoute>} />
    <Route path="/pending/:agreementID" element={<ProtectedRoute><PendingConfirmationPage /></ProtectedRoute>} />
    <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
    <Route path="/account" element={<ProtectedRoute><AccountPage /></ProtectedRoute>} />

    {/* 404 */}
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

export default function App() {
  return (
    <ErrorBoundary>
      <HashRouter>
        <AuthProvider>
          <AppRoutes />
          <Footer />
          <SmartAppBanner />
        </AuthProvider>
      </HashRouter>
    </ErrorBoundary>
  );
}
